import type {
  DeadlineDomainValue,
  DeadlineRefDto,
  DocumentSignatureRequestValue,
} from '@prospero/shared';
import { inject } from 'inversify';
import { formatDateKey } from '../../components/calendar/calendar-utils';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import { type IKyClientService, KyClientService } from '../ky-client/ky-client.service';

export type DocumentCategory = string;

export const ALLOWED_EXPIRY_WARNING_DAYS = [7, 14, 30] as const;
export const DEFAULT_EXPIRY_WARNING_DAYS = 7;
export type ExpiryWarningDays = (typeof ALLOWED_EXPIRY_WARNING_DAYS)[number];

interface DocumentTypeRef {
  id: string;
  name: string;
}

export interface PreviewPage {
  pageNumber: number;
  storageKey: string;
  width: number;
  height: number;
  url: string;
}

interface PreviewResult {
  sourceKey: string;
  pages: PreviewPage[];
  totalPages: number;
}

interface DocumentUploadResult {
  documentId: string;
  originalFilename: string;
  category: DocumentCategory;
  previews: PreviewResult;
  expiresAt: string | null;
}

export interface DocumentInfo {
  id: string;
  originalFilename: string;
  status: string;
  category: DocumentCategory;
  previews: PreviewResult;
  signedStorageKey: string | null;
  documentType?: DocumentTypeRef;
  expiresAt: string | null;
}

export interface SignaturePosition {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SignedDocumentResult {
  documentId: string;
  signedStorageKey: string;
  downloadUrl: string;
}

export type ComplianceSourceType =
  | 'TEMPERATURE'
  | 'CLEANING'
  | 'OIL'
  | 'PEST_CONTROL'
  | 'BLAST_CHILLING'
  | 'COOKING_TEMPERATURE';

export interface ComplianceLink {
  linkType: 'NON_CONFORMITY' | 'SELF_CHECK';
  sourceType: ComplianceSourceType;
  recordDate: string;
}

export interface DocumentSummary {
  id: string;
  originalFilename: string;
  status: 'PENDING' | 'SIGNED';
  /**
   * Chi deve firmare: `NONE` = file dello scadenzario solo caricato ("Caricato"), `COMPANY` = firma
   * chiesta all'azienda (il Consulente non firma). Assente (risposte in cache) = `EXPECTED`.
   */
  signatureRequest?: DocumentSignatureRequestValue;
  category: DocumentCategory;
  createdAt: string;
  /** Full name (email fallback) of who uploaded it; null when that user was deleted. */
  uploaderName: string | null;
  signedStorageKey: string | null;
  documentType?: DocumentTypeRef;
  expiresAt: string | null;
  expiryWarningDays: number | null;
  /** Non-null when the document has been archived (soft-hidden from the default list). */
  archivedAt: string | null;
  /** Non-null when the document is attached to a non-conformity or self-check record. */
  complianceLink: ComplianceLink | null;
  /** Names of the lots this document is attached to (empty/absent when none). */
  lotNames?: string[];
  /** Scadenza collegata (bucket calcolato dal server); assente/null per i documenti senza scadenza. */
  deadline?: DeadlineRefDto | null;
}

export type DocumentsSortOption = 'createdAt' | 'expiresAt';

interface UploadDocumentOptions {
  documentTypeId?: string;
  expiresAt?: Date | null;
  expiryWarningDays?: ExpiryWarningDays | null;
  /** Solo tipi personali con scadenza: il backend risponde 422 altrimenti. */
  assigneeUserId?: string;
  /** Opt-in for surfaces (e.g. survey file-upload node) that also accept jpg/png. */
  allowImages?: boolean;
}

export interface DocumentsListFilters {
  category?: DocumentCategory;
  q?: string;
  archived?: boolean;
  status?: 'PENDING' | 'SIGNED';
  from?: string;
  to?: string;
  /** Restricts results to documents whose expiresAt falls within the range. */
  expiryFrom?: string;
  expiryTo?: string;
  page?: number;
  pageSize?: number;
  /** `expiresAt` = scadenza piu' vicina prima, senza scadenza in coda; default: piu' recenti prima. */
  sort?: DocumentsSortOption;
  /** Filtro, non raggruppamento: restringe la lista alle scadenze di una persona. */
  assigneeUserId?: string;
  /** Ambito della scadenza collegata (sicurezza alimentare, sul lavoro, legale). */
  domain?: DeadlineDomainValue;
  /** Solo i documenti attivi in questa situazione; il server li ordina per scadenza piu' vicina. */
  expiry?: DocumentsExpiryFilter;
}

/**
 * `counts` drives the filter pills, so it deliberately ignores the archived
 * toggle and the status filter — it answers "how many are in the other tabs".
 */
export interface DocumentCounts {
  /** "Da firmare": non firmati con una firma attesa. */
  pending: number;
  signed: number;
  /** "Caricato": non firmati che nessuno deve firmare. Assente nelle risposte in cache. */
  uploaded?: number;
  archived: number;
  /** Documenti attivi con il ciclo di scadenza corrente scaduto. */
  expired: number;
  /** Documenti attivi con il ciclo di scadenza corrente in scadenza. */
  expiring: number;
}

export type DocumentsExpiryFilter = 'EXPIRED' | 'EXPIRING';

export interface DocumentsPage {
  items: DocumentSummary[];
  /** Documents matching the filters, ignoring pagination. */
  total: number;
  page: number;
  pageSize: number;
  counts: DocumentCounts;
}

export interface IDocumentSignatureService {
  findAll(filters?: DocumentsListFilters): Promise<DocumentsPage>;
  findAllByTenant(category?: DocumentCategory, q?: string): Promise<DocumentSummary[]>;
  uploadDocument(file: File, options?: UploadDocumentOptions): Promise<DocumentUploadResult>;
  getDocument(documentId: string): Promise<DocumentInfo>;
  signDocument(documentId: string, signatures: SignaturePosition[]): Promise<SignedDocumentResult>;
  getDownloadUrl(documentId: string): Promise<string>;
  deleteDocument(documentId: string): Promise<void>;
  archiveDocument(documentId: string): Promise<DocumentSummary>;
  unarchiveDocument(documentId: string): Promise<DocumentSummary>;
  /**
   * Links this document as the replacement of an older one in the same
   * category (supersession): the old document is soft-archived. 409 on
   * cycles, category mismatch or replacement not SIGNED.
   */
  supersedeDocument(documentId: string, supersededId: string): Promise<DocumentSummary>;
  updateExpiry(
    documentId: string,
    expiresAt: Date | null,
    expiryWarningDays?: ExpiryWarningDays | null,
    assigneeUserId?: string | null,
  ): Promise<DocumentSummary>;
}

/** Skips unset values (undefined, empty string, 0) so the query string stays minimal. */
function setPresentParams(
  params: URLSearchParams,
  entries: Record<string, string | number | undefined>,
): void {
  for (const [name, value] of Object.entries(entries)) {
    if (value) {
      params.set(name, String(value));
    }
  }
}

function buildDocumentsQuery(
  category?: DocumentCategory,
  q?: string,
  archived?: boolean,
  from?: string,
  to?: string,
  pagination?: {
    status?: 'PENDING' | 'SIGNED';
    page?: number;
    pageSize?: number;
    sort?: DocumentsSortOption;
    expiry?: DocumentsExpiryFilter;
  },
  expiryFrom?: string,
  expiryTo?: string,
  assigneeUserId?: string,
  domain?: DeadlineDomainValue,
): string {
  const params = new URLSearchParams();
  if (category) {
    params.set('category', category);
  }
  if (q?.trim()) {
    params.set('q', q.trim());
  }
  if (archived) {
    params.set('archived', 'true');
  }
  if (from) {
    params.set('from', from);
  }
  if (to) {
    params.set('to', to);
  }
  if (expiryFrom) {
    params.set('expiryFrom', expiryFrom);
  }
  if (expiryTo) {
    params.set('expiryTo', expiryTo);
  }
  if (assigneeUserId) {
    params.set('assigneeUserId', assigneeUserId);
  }
  if (domain) {
    params.set('domain', domain);
  }
  setPresentParams(params, {
    status: pagination?.status,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    sort: pagination?.sort !== 'createdAt' ? pagination?.sort : undefined,
    expiry: pagination?.expiry,
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

@Injectable()
class DocumentSignatureService implements IDocumentSignatureService {
  constructor(
    @inject(HttpService) private readonly httpService: IHttpService,
    @inject(KyClientService) private readonly kyClientService: IKyClientService,
  ) {}

  async findAll(filters: DocumentsListFilters = {}): Promise<DocumentsPage> {
    const params = buildDocumentsQuery(
      filters.category,
      filters.q,
      filters.archived,
      filters.from,
      filters.to,
      {
        status: filters.status,
        page: filters.page,
        pageSize: filters.pageSize,
        sort: filters.sort,
        expiry: filters.expiry,
      },
      filters.expiryFrom,
      filters.expiryTo,
      filters.assigneeUserId,
      filters.domain,
    );
    return this.httpService.get<DocumentsPage>(`documents${params}`);
  }

  async findAllByTenant(category?: DocumentCategory, q?: string): Promise<DocumentSummary[]> {
    const params = buildDocumentsQuery(category, q);
    return this.httpService.get<DocumentSummary[]>(`documents/tenant${params}`);
  }

  async uploadDocument(
    file: File,
    options: UploadDocumentOptions = {},
  ): Promise<DocumentUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.documentTypeId) {
      formData.append('documentTypeId', options.documentTypeId);
    }
    if (options.expiresAt) {
      formData.append('expiresAt', formatDateKey(options.expiresAt));
      if (options.expiryWarningDays != null) {
        formData.append('expiryWarningDays', String(options.expiryWarningDays));
      }
      if (options.assigneeUserId) {
        formData.append('assigneeUserId', options.assigneeUserId);
      }
    }

    const url = options.allowImages ? 'documents/upload?allowImages=true' : 'documents/upload';
    const client = this.kyClientService.getClient();
    return client.post(url, { body: formData }).json<DocumentUploadResult>();
  }

  async getDocument(documentId: string): Promise<DocumentInfo> {
    return this.httpService.get<DocumentInfo>(`documents/${documentId}`);
  }

  async signDocument(
    documentId: string,
    signatures: SignaturePosition[],
  ): Promise<SignedDocumentResult> {
    return this.httpService.post<SignedDocumentResult>(`documents/${documentId}/sign`, {
      signatures,
    });
  }

  async getDownloadUrl(documentId: string): Promise<string> {
    const { url } = await this.httpService.get<{ url: string }>(
      `documents/${documentId}/signed-url`,
    );
    return url;
  }

  async deleteDocument(documentId: string): Promise<void> {
    await this.httpService.delete(`documents/${documentId}`);
  }

  async archiveDocument(documentId: string): Promise<DocumentSummary> {
    return this.httpService.post<DocumentSummary>(`documents/${documentId}/archive`, {});
  }

  async unarchiveDocument(documentId: string): Promise<DocumentSummary> {
    return this.httpService.post<DocumentSummary>(`documents/${documentId}/unarchive`, {});
  }

  async supersedeDocument(documentId: string, supersededId: string): Promise<DocumentSummary> {
    return this.httpService.post<DocumentSummary>(`documents/${documentId}/supersede`, {
      supersededId,
    });
  }

  async updateExpiry(
    documentId: string,
    expiresAt: Date | null,
    expiryWarningDays?: ExpiryWarningDays | null,
    assigneeUserId?: string | null,
  ): Promise<DocumentSummary> {
    return this.httpService.patch<DocumentSummary>(`documents/${documentId}/expiry`, {
      expiresAt: expiresAt ? formatDateKey(expiresAt) : null,
      ...(expiresAt && expiryWarningDays != null && { expiryWarningDays }),
      ...(assigneeUserId !== undefined && { assigneeUserId }),
    });
  }
}

export { DocumentSignatureService };
